'use client';

import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { theme } from '@/config/theme';
import { Skeleton } from '@/components/skeletons';
import { ExternalLinkIcon } from '@/components/icons';

export interface MonitorInfoHeaderProps {
  /** Main title/name to display */
  title: string;
  /** URL to link to */
  url: string;
  /** Icon component to display */
  icon: React.ReactNode;
  /** Protocol/type label (e.g., "HTTP/S", "Page Scraper") */
  protocolLabel: string;
  /** Additional action buttons/content to display on the right */
  children?: React.ReactNode;
  /** Optional className for styling */
  className?: string;
  /** When 'breadcrumb', title uses same style as breadcrumb headings (14px, uppercase, letter-spacing 0.24em) */
  titleVariant?: 'default' | 'breadcrumb';
  /** Show loading skeleton when true */
  isLoading?: boolean;
}

const MonitorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  gap: 24px;
  border-bottom: 1px solid ${theme.colors.border.primary};
  border-top: 1px solid ${theme.colors.border.primary};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MonitorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const MonitorIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MonitorDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const MonitorUrl = styled.div<{ $breadcrumbStyle?: boolean }>`
  font-size: ${({ $breadcrumbStyle }) => ($breadcrumbStyle ? '14px' : '16px')};
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  color: ${theme.colors.gray.darkText};
  margin-bottom: 2px;
  word-break: break-all;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ $breadcrumbStyle }) =>
    $breadcrumbStyle &&
    `
    letter-spacing: 0.24em;
    text-transform: uppercase;
  `}

  svg {
    width: 16px;
    height: 16px;
    color: ${theme.colors.gray[400]};
    flex-shrink: 0;
  }
`;

const URLSubtitle = styled.div`
  font-family: 'Open Sans', sans-serif;
  font-size: 10px;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0;
  color: ${theme.colors.gray.darkText};
  word-break: break-all;
`;

const URLSubtitleProtocol = styled.span`
  font-weight: 700;
`;

const URLSubtitleProtocolLink = styled(Link)`
  color: ${theme.colors.status.successAlt};
  text-decoration: underline;
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  button, a button {
    svg {
      flex-shrink: 0;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

/**
 * MonitorInfoHeader Component
 * 
 * Displays monitor information header with icon, title, URL, protocol label, and action buttons.
 * Used across monitor detail pages to show consistent header layout.
 * 
 * @example
 * ```tsx
 * <MonitorInfoHeader
 *   title="https://example.com"
 *   url="https://example.com"
 *   icon={<StatusUpIcon size={32} color="#14AE5C" />}
 *   protocolLabel="HTTP/S"
 * >
 *   <Button>Edit</Button>
 *   <Button>Delete</Button>
 * </MonitorInfoHeader>
 * ```
 */
export const MonitorInfoHeader: React.FC<MonitorInfoHeaderProps> = ({
  title,
  url,
  icon,
  protocolLabel,
  children,
  className,
  titleVariant = 'default',
  isLoading = false,
}) => {
  return (
    <MonitorHeader className={className}>
      <MonitorInfo>
        <MonitorIcon>
          {isLoading ? (
            <Skeleton width={32} height={32} variant="circular" animation="pulse" />
          ) : (
            icon
          )}
        </MonitorIcon>
        <MonitorDetails>
          {isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Skeleton width="200px" height="20px" variant="text" animation="pulse" />
              <Skeleton width="300px" height="12px" variant="text" animation="pulse" />
            </div>
          ) : (
            <>
              <MonitorUrl $breadcrumbStyle={titleVariant === 'breadcrumb'}>
                {title}
                <Link href={url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${url} in new tab`}>
                  <ExternalLinkIcon size={16} />
                </Link>
              </MonitorUrl>
              <URLSubtitle>
                <URLSubtitleProtocol>{protocolLabel}</URLSubtitleProtocol> monitor for{' '}
                <URLSubtitleProtocolLink href={url} target="_blank" rel="noopener noreferrer">
                  {url}
                </URLSubtitleProtocolLink>
              </URLSubtitle>
            </>
          )}
        </MonitorDetails>
      </MonitorInfo>

      {children && <Actions>{children}</Actions>}
    </MonitorHeader>
  );
};

export default MonitorInfoHeader;

